import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import FirstTimerForm from './components/FirstTimerForm';
import SubmissionSuccess from './components/SubmissionSuccess';
import Login from './components/Login';
import Signup from './components/Signup';
import WelcomePage from './components/WelcomePage';
import FollowUpDashboard from './components/FollowUpDashboard';
import FirstTimerDetail from './components/FirstTimerDetail';
import UserManagement from './components/UserManagement';
import OrganizationManager from './components/OrganizationManager';
import OrganizationSettings from './components/OrganizationSettings';
import MasterDashboard from './components/MasterDashboard';
import CommunicationAnalytics from './components/CommunicationAnalytics';
import Navigation from './components/Navigation';
import { isAuthRoute } from './utils/isAuthRoute';
import jwt_decode from 'jwt-decode';
import { Link } from 'react-router-dom';
import AssignmentDashboard from './components/AssignmentDashboard';
import MyTasks from './components/MyTasks';
import SignUpSubmissionSuccess from './components/SignUpSubmissionSuccess';
import UserProfile from './components/UserProfile';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import NewConvertForm from './components/NewConvertForm';
import NewConvertDashboard from './components/NewConvertDashboard';
import NewConvertThankYou from './components/NewConvertThankYou';
import NewConvertDetail from './components/NewConvertDetail';
import AssignNewConverts from './components/AssignNewConverts';
import MyNewConverts from './components/MyNewConverts';
import SMSDashboard from './components/SMSDashboard';
import Harvest from './components/Harvest';
import FoundationSchoolForm from './components/FoundationSchoolForm';
import FoundationSchoolDashboard from './components/FoundationSchoolDashboard';
import FoundationSchoolDetail from './components/FoundationSchoolDetail';
import FoundationSchoolSuccess from './components/FoundationSchoolSuccess';
import AssignFoundationSchool from './components/AssignFoundationSchool';
import PublicHarvestForm from './components/PublicHarvestForm';
import EmailDashboard from './components/EmailDashboard';
import EmailComposer from './components/EmailComposer';
import OrganizationRoute from './components/OrganizationRoute';
import NotFound from './components/NotFound';
import { AuthProvider, useAuth } from './context/AuthContext';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#DC2626', // Vibrant red from WSF Manager
      light: '#EF4444',
      dark: '#B91C1C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#991B1B', // Darker red variant
      light: '#DC2626',
      dark: '#7F1D1D',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10b981', // Modern green
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b', // Modern amber
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#DC2626', // Same as primary red
      light: '#EF4444',
      dark: '#B91C1C',
    },
    info: {
      main: '#06b6d4', // Modern cyan
      light: '#22d3ee',
      dark: '#0891b2',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#DC2626',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#DC2626',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: '#f8fafc',
            borderBottom: '2px solid #e2e8f0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e2e8f0',
          padding: '16px',
        },
      },
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

const ProtectedManagerRoute = ({ children }) => {
  const { user } = useAuth();
  const isManager = user?.role === 'unit_manager' || user?.role === 'system_admin';

  if (!isManager) {
    return <Navigate to="/my-tasks" />;
  }

  return children;
};

const ProtectedSuperAdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const isPlatformSuperAdmin =
    user?.role === 'system_admin' && !user?.organization_id;
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  if (!isPlatformSuperAdmin) {
    if (user?.organization_slug) {
      return <Navigate to={`/${user.organization_slug}/dashboard`} replace />;
    }
    return <Navigate to="/not-found" replace />;
  }

  return children;
};

function AppRoutes() {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const location = useLocation();
  
  // Check if we're on an organization-specific page (has organization slug in URL)
  const hasOrganizationSlug = location.pathname.match(/^\/([^/]+)\//) && 
                               !['admin', 'master-dashboard', 'organization-management', 'user-management'].includes(location.pathname.split('/')[1]);
  
  // Auth pages stay focused and do not show the application navigation.
  const isLoginPage = isAuthRoute(location.pathname);
  
  // Show navigation if:
  // 1. Authenticated AND not on login/signup pages AND not on welcome page, OR
  // 2. Not authenticated BUT on an organization-specific page (including login/signup) - so they can access forms
  const showNavigation = !isLoginPage && (
    (isAuthenticated && location.pathname !== '/') ||
    (!isAuthenticated && hasOrganizationSlug)
  );

  // Show loading spinner while authentication state is being restored
  if (isLoading) {
    console.log('App: Still loading authentication state...');
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #DC2626',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#666', fontSize: '16px' }}>Loading...</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  console.log('App: Authentication loaded, evaluating routes...', { isAuthenticated, user: user?.role });
  
  return (
    <>
      {showNavigation && (
        <Navigation 
          isAuthenticated={isAuthenticated} 
          onLogout={logout}
          user={user}
        />
      )}
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              user?.role === 'unit_member' ? (
                (() => {
                  console.log('Root route: Redirecting unit member to /my-tasks');
                  // If user has organization_id, redirect to org-specific route
                  if (user?.organization_id && user?.organization_slug) {
                    return <Navigate to={`/${user.organization_slug}/my-tasks`} replace />;
                  }
                  return <Navigate to="/my-tasks" replace />;
                })()
              ) : (
                (() => {
                  console.log('Root route: Redirecting manager to dashboard');
                  if (user?.role === 'system_admin' && !user?.organization_id) {
                    return <Navigate to="/master-dashboard" replace />;
                  }
                  if (user?.role === 'system_admin' && user?.organization_id) {
                    return <Navigate to="/admin/dashboard" replace />;
                  }
                  if (user?.role === 'unit_manager' && user?.organization_slug) {
                    return <Navigate to={`/${user.organization_slug}/dashboard`} replace />;
                  }
                  return <Navigate to="/not-found" replace />;
                })()
              )
            ) : (
              <WelcomePage />
            )
          }
        />
        <Route 
          path="/login" 
          element={
            isAuthenticated ? 
              user?.role === 'unit_member' ?
                (user?.organization_slug ? (
                  <Navigate to={`/${user.organization_slug}/my-tasks`} replace />
                ) : (
                  <Navigate to="/" replace />
                )) :
              user?.role === 'unit_manager' && user?.organization_slug ?
                <Navigate to={`/${user.organization_slug}/dashboard`} replace /> :
              user?.role === 'system_admin' && !user?.organization_id ?
                <Navigate to="/master-dashboard" replace /> :
              user?.role === 'system_admin' && user?.organization_id ?
                <Navigate to="/admin/dashboard" replace /> :
              <Navigate to="/" replace /> :
              <Navigate to="/" replace />
          } 
        />
        
        {/* Admin/Master Dashboard Login - No organization context */}
        <Route 
          path="/admin/login" 
          element={
            isAuthenticated ? 
              // If system_admin logging in from /admin/login, always go to master dashboard
              (user?.role === 'system_admin') ?
              <Navigate to="/master-dashboard" replace /> :
              // Otherwise, redirect to their organization dashboard if they have one
              (user?.organization_id && user?.organization_slug) ?
              <Navigate to={`/${user.organization_slug}/dashboard`} replace /> :
              <Navigate to="/" replace /> :
              <Login />
          } 
        />
        <Route 
          path="/signup" 
          element={
            isAuthenticated ? 
              user?.role === 'unit_member' ?
                (user?.organization_slug ? (
                  <Navigate to={`/${user.organization_slug}/my-tasks`} replace />
                ) : (
                  <Navigate to="/" replace />
                )) :
              user?.role === 'unit_manager' && user?.organization_slug ?
                <Navigate to={`/${user.organization_slug}/dashboard`} replace /> :
              user?.role === 'system_admin' && !user?.organization_id ?
                <Navigate to="/master-dashboard" replace /> :
              user?.role === 'system_admin' && user?.organization_id ?
                <Navigate to="/admin/dashboard" replace /> :
              user?.organization_slug ?
                <Navigate to={`/${user.organization_slug}/dashboard`} replace /> :
              <Navigate to="/" replace /> :
              <Signup />
          } 
        />
        {/* Legacy protected routes without organization prefix - redirect to welcome page */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/dashboard/*" element={<Navigate to="/" replace />} />
        <Route path="/first-timer/:id" element={<Navigate to="/" replace />} />
        
        {/* Super-admin routes (no organization prefix required) */}
        <Route 
          path="/user-management" 
          element={
            user?.organization_id && user?.organization_slug ? (
              <Navigate to={`/${user.organization_slug}/user-management`} replace />
            ) : (
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <UserManagement />
              </ProtectedManagerRoute>
            </ProtectedRoute>
            )
          } 
        />
        <Route 
          path="/organization-management" 
          element={
            user?.organization_id && user?.organization_slug ? (
              <Navigate to={`/${user.organization_slug}/organization-settings`} replace />
            ) : (
            <ProtectedRoute>
              <ProtectedSuperAdminRoute>
                <OrganizationManager />
              </ProtectedSuperAdminRoute>
            </ProtectedRoute>
            )
          } 
        />
        
        {/* Master Dashboard for super-admins only */}
        <Route 
          path="/master-dashboard" 
          element={
            <ProtectedSuperAdminRoute>
              <MasterDashboard />
            </ProtectedSuperAdminRoute>
          } 
        />
        <Route
          path="/communication-analytics"
          element={
            <ProtectedSuperAdminRoute>
              <CommunicationAnalytics />
            </ProtectedSuperAdminRoute>
          }
        />
        
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute>
              <ProtectedManagerRoute>
                {user?.role === 'system_admin' && user?.organization_id && user?.organization_slug ? (
                  <Navigate to={`/${user.organization_slug}/dashboard`} replace />
                ) : user?.role === 'unit_manager' && user?.organization_slug ? (
                  <Navigate to={`/${user.organization_slug}/dashboard`} replace />
                ) : user?.role === 'system_admin' && !user?.organization_id ? (
                  <Navigate to="/master-dashboard" replace />
                ) : (
                  <Navigate to="/not-found" replace />
                )}
              </ProtectedManagerRoute>
            </ProtectedRoute>
          } 
        />
        {/* Legacy protected routes without organization prefix - redirect to welcome page */}
        <Route path="/organization-settings" element={<Navigate to="/" replace />} />
        <Route path="/assignments" element={<Navigate to="/" replace />} />
        <Route path="/my-tasks" element={<Navigate to="/" replace />} />
        {/* Legacy success routes - redirect to welcome page */}
        <Route path="/submission-success" element={<Navigate to="/" replace />} />
        <Route path="/signup-success" element={<Navigate to="/" replace />} />
        
        {/* Organization-prefixed success routes */}
        <Route path="/:organizationSlug/submission-success" element={
          <OrganizationRoute>
            <SubmissionSuccess />
          </OrganizationRoute>
        } />
        <Route path="/:organizationSlug/signup-success" element={
          <OrganizationRoute>
            <SignUpSubmissionSuccess />
          </OrganizationRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        } />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/not-found" element={<NotFound />} />
        
        {/* Legacy routes without organization prefix - redirect to welcome page */}
        <Route path="/add-first-timer" element={<Navigate to="/" replace />} />
        <Route path="/new-convert" element={<Navigate to="/" replace />} />
        <Route path="/harvest" element={<Navigate to="/" replace />} />
        <Route path="/new-converts" element={<Navigate to="/" replace />} />
        <Route path="/new-converts/:id" element={<Navigate to="/" replace />} />
        <Route path="/assign-new-converts" element={<Navigate to="/" replace />} />
        <Route path="/thank-you" element={<Navigate to="/" replace />} />
        <Route path="/my-new-converts" element={<Navigate to="/" replace />} />
        <Route path="/sms" element={<Navigate to="/" replace />} />
        <Route path="/harvest-system" element={<Navigate to="/" replace />} />
        {/* Legacy protected routes without organization prefix - redirect to welcome page */}
        <Route path="/email-campaigns" element={<Navigate to="/" replace />} />
        <Route path="/email-composer" element={<Navigate to="/" replace />} />
        
        {/* Organization-prefixed routes: /:organizationSlug/* */}
        <Route path="/:organizationSlug/signup" element={
          <OrganizationRoute>
            {isAuthenticated ? 
              user?.role === 'unit_member' ?
              <Navigate to={`/${window.location.pathname.split('/')[1]}/my-tasks`} replace /> :
              <Navigate to={`/${window.location.pathname.split('/')[1]}/dashboard`} replace /> :
              <Signup />
            }
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/login" element={
          <OrganizationRoute>
            {isAuthenticated ? 
              user?.role === 'unit_member' ?
              <Navigate to={`/${window.location.pathname.split('/')[1]}/my-tasks`} replace /> :
              <Navigate to={`/${window.location.pathname.split('/')[1]}/dashboard`} replace /> :
              <Login />
            }
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/dashboard" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <FollowUpDashboard />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/my-tasks" element={
          <OrganizationRoute>
            {isAuthenticated ? 
              <MyTasks /> : 
              <Navigate to={`/${window.location.pathname.split('/')[1]}/login`} />
            }
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/first-timer/:id" element={
          <OrganizationRoute>
            {isAuthenticated ? <FirstTimerDetail /> : <Navigate to={`/${window.location.pathname.split('/')[1]}/login`} />}
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/user-management" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <UserManagement />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/organization-management" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <OrganizationManager />
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/organization-settings" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <OrganizationSettings />
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/assignments" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <AssignmentDashboard />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/new-converts" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <NewConvertDashboard />
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/new-converts/:id" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <NewConvertDetail />
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/assign-new-converts" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <AssignNewConverts />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/my-new-converts" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <MyNewConverts />
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/sms" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <SMSDashboard />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/harvest-system" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <Harvest />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/email-campaigns" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <EmailDashboard />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/email-composer" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <EmailComposer />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/profile" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/add-first-timer" element={
          <OrganizationRoute>
            <FirstTimerForm />
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/new-convert" element={
          <OrganizationRoute>
            <NewConvertForm />
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/foundation-school" element={
          <OrganizationRoute>
            <FoundationSchoolForm />
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/foundation-school-dashboard" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <FoundationSchoolDashboard />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/assign-foundation-school" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <ProtectedManagerRoute>
                <AssignFoundationSchool />
              </ProtectedManagerRoute>
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/foundation-school/:id" element={
          <OrganizationRoute>
            <ProtectedRoute>
              <FoundationSchoolDetail />
            </ProtectedRoute>
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/harvest" element={
          <OrganizationRoute>
            <PublicHarvestForm />
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/submission-success" element={
          <OrganizationRoute>
            <SubmissionSuccess />
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/thank-you" element={
          <OrganizationRoute>
            <NewConvertThankYou />
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug/foundation-school-success" element={
          <OrganizationRoute>
            <FoundationSchoolSuccess />
          </OrganizationRoute>
        } />
        
        <Route path="/:organizationSlug" element={
          <OrganizationRoute>
            {isAuthenticated ? (
              user?.role === 'unit_member' ? (
                <Navigate to={`/${window.location.pathname.split('/')[1]}/my-tasks`} replace />
              ) : (
                <Navigate to={`/${window.location.pathname.split('/')[1]}/dashboard`} replace />
              )
            ) : (
              <FirstTimerForm />
            )}
          </OrganizationRoute>
        } />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
